import { getSocialConnector, platformTintStyle } from '@lib/social/scheduling/platforms';
import type { SocialPost } from '@lib/social/scheduling/posts';
import { scheduleSettings } from '@stores/ScheduleSettings';
import classNames from 'classnames';
import { format, isThisYear } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { postPath } from '../../schedulePath';
import { StatusPill } from '../StatusPill/StatusPill';
import css from './PostChip.module.scss';

/**
 * One post as a tinted chip: when, the platform, the headline, the status.
 * With `link` set it is a link to the post's page with the labelled status
 * pill; a popover list has the room for both. Without it the chip stays a
 * plain row with the status dot: a calendar cell is one button, and a link
 * inside it would nest two interactive elements.
 */
export const PostChip = observer(function PostChip({
  post,
  link,
  showDate = false,
}: {
  post: SocialPost;
  /** Where the post's page opens; a new tab for a list beside a form that must keep what was typed. */
  link?: 'same-tab' | 'new-tab';
  /** Prefix the time with the day (and the year, once it is not this one), for a list that spans more than one day. */
  showDate?: boolean;
}) {
  const connector = getSocialConnector(post.platform);
  const text = post.summary?.headline ?? post.platform;
  const at = scheduleSettings.inZone(post.publishAt);
  const body = (
    <>
      {!link && <StatusPill status={post.status} compact />}
      <span className={css.time}>
        {format(at, !showDate ? 'p' : isThisYear(at) ? 'MMM d, p' : 'MMM d, yyyy, p')}
      </span>
      <span className={css.icon}>{connector?.icon}</span>
      <span className={css.title}>{text}</span>
      {link && (
        <span className={css.status}>
          <StatusPill status={post.status} />
        </span>
      )}
    </>
  );
  const chipProps = {
    className: classNames(css.chip, link && css.linked),
    style: platformTintStyle(connector),
    'data-status': post.status,
    title: text,
  };
  return link ? (
    <Link
      to={postPath(post.id)}
      {...chipProps}
      {...(link === 'new-tab' && { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {body}
    </Link>
  ) : (
    <div {...chipProps}>{body}</div>
  );
});
